import { keepPreviousData, useQuery } from '@tanstack/react-query';
import * as fieldSalesApi from '../api/fieldSalesApi.js';
import { useFieldSalesFilters } from './useFieldSalesFilters.js';

// Per-endpoint query-key constants. Each section/tile has its OWN key so a
// failure on one query only fails that one section — the rest of the page keeps
// rendering. Filters go LAST in every key so changing filters refetches.
const FS_SUMMARY_KEY = 'field-sales-summary';
const FS_BREAKDOWN_KEY = 'field-sales-breakdown';
const FS_TREND_KEY = 'field-sales-trend';
const FS_HQ_PRODUCTIVITY_KEY = 'field-sales-hq-productivity';
const FS_MATRIX_KEY = 'field-sales-matrix';
const FS_CRITICAL_KEY = 'field-sales-critical';
const FS_VOLATILITY_KEY = 'field-sales-volatility';
const FS_CREDIT_NOTES_KEY = 'field-sales-credit-notes';
const FS_FILTERS_KEY = 'field-sales-filters';
const FS_BREAKDOWN_PAGED_KEY = 'field-sales-breakdown-paged';
const FS_SUMMARY_HQS_BAND_KEY = 'field-sales-summary-hqs-band';
const FS_HQ_PRODUCTIVITY_N_KEY = 'field-sales-hq-productivity-n';
const FS_MATRIX_QUAD_KEY = 'field-sales-matrix-quad';
const FS_CREDIT_NOTES_TYPE_KEY = 'field-sales-credit-notes-type';

/** Convert a wire month "YYYYMM" to the UI/URL shape "YYYY-MM" (pass-through otherwise). */
function monthToUi(ym) {
  if (!ym) return null;
  const m = /^(\d{4})(\d{2})$/.exec(String(ym));
  return m ? `${m[1]}-${m[2]}` : String(ym);
}

/**
 * Applied Field Sales multi-select filter selection plus the commit/reset
 * actions. The filter bar reads this, keeps its draft selection locally, and
 * calls `applySelection` on Apply (so the dashboard hooks only refetch when
 * Apply is clicked) or `resetSelection` on Reset.
 */
export function useFieldSalesFilterState() {
  const applied = useFieldSalesFilters((s) => s.applied);
  const applySelection = useFieldSalesFilters((s) => s.applySelection);
  const resetSelection = useFieldSalesFilters((s) => s.resetSelection);
  return { applied, applySelection, resetSelection };
}

/**
 * Filter options for the bar: the full option lists from filters.json plus the
 * `months_available` list converted to the UI/URL shape "YYYY-MM" and the
 * derived `latestMonth` (the newest available month) used as the default
 * selection. One request, cached — the lists are static for the dashboard.
 *
 * @returns {{
 *   fiscalYear: string[], division: string[], zone: string[], state: string[],
 *   category: string[], brand: string[], hq: string[], bdm: string[],
 *   zm: string[], monthsAvailable: string[], latestMonth: string|null,
 *   isLoading: boolean,
 * }}
 */
// Normalise an option list to plain, non-empty string[]. The dropdowns are
// string-based, so an entry that arrives as an object ({name}/{label}/{value})
// — or a number — is coerced to its label; anything empty is dropped. This
// keeps every filter (incl. BDM/ZM) working regardless of the item shape.
function toStrOptions(list) {
  return (Array.isArray(list) ? list : [])
    .map((o) => {
      if (o == null) return '';
      if (typeof o === 'object') return String(o.label ?? o.name ?? o.value ?? o.id ?? '');
      return String(o);
    })
    .map((s) => s.trim())
    .filter(Boolean);
}

export function useFieldSalesFilterOptions() {
  const query = useQuery({
    queryKey: [FS_FILTERS_KEY],
    queryFn: () => fieldSalesApi.getFilters(),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    select: (data) => {
      // months_available is YYYYMM, newest-last in the sample; sort descending
      // on the raw wire value so `latest` is the highest YYYYMM, then expose
      // the UI/URL YYYY-MM shape.
      const wire = [...(data?.monthsAvailable ?? [])].filter(Boolean);
      const sortedDesc = wire.map(String).sort((a, b) => b.localeCompare(a));
      const monthsAvailableUi = sortedDesc.map(monthToUi).filter(Boolean);
      return {
        fiscalYear: toStrOptions(data?.fiscalYear),
        division: toStrOptions(data?.division),
        zone: toStrOptions(data?.zone),
        state: toStrOptions(data?.state),
        category: toStrOptions(data?.category),
        brand: toStrOptions(data?.brand),
        hq: toStrOptions(data?.hq),
        bdm: toStrOptions(data?.bdm),
        zm: toStrOptions(data?.zm),
        monthsAvailable: monthsAvailableUi,
        latestMonth: monthsAvailableUi[0] ?? null,
      };
    },
  });

  const d = query.data ?? {};
  return {
    fiscalYear: d.fiscalYear ?? [],
    division: d.division ?? [],
    zone: d.zone ?? [],
    state: d.state ?? [],
    category: d.category ?? [],
    brand: d.brand ?? [],
    hq: d.hq ?? [],
    bdm: d.bdm ?? [],
    zm: d.zm ?? [],
    monthsAvailable: d.monthsAvailable ?? [],
    latestMonth: d.latestMonth ?? null,
    isLoading: query.isLoading || query.isFetching,
  };
}

/**
 * The month(s) the backend actually served for the current filters, as UI
 * "YYYY-MM" strings. Read from the summary response's `months` (shares the
 * summary query key, so it dedupes with useFieldSales — no extra request).
 *
 * This is the fix for the month default: the filter bar sends NO months param
 * when the user hasn't chosen one, the backend resolves to its latest month
 * WITH sales, and this exposes that resolved month so the UI can show its real
 * name and pre-select it in the Month filter.
 *
 * @returns {{ months: string[], isLoading: boolean }}
 */
export function useFieldSalesResolvedMonths() {
  const filters = useFieldSalesFilters((s) => s.applied);
  const query = useQuery({
    queryKey: [FS_SUMMARY_KEY, filters],
    queryFn: () => fieldSalesApi.getSummary(filters),
    select: (data) => (Array.isArray(data?.months) ? data.months : []),
  });
  return {
    months: (query.data ?? []).map(monthToUi).filter(Boolean),
    isLoading: query.isLoading || query.isFetching,
  };
}

/**
 * Load the Field Sales dashboard for the currently APPLIED filters, LAZILY.
 *
 * Each endpoint is its own independent `useQuery` with its own key, so a failed
 * OPTIONAL call never blanks the page — only that one section surfaces an error
 * (via its `isError`/`error`), while the others keep rendering their data. The
 * `summary` query is the single REQUIRED call: it drives the page-level KPI
 * cards, Auto Insights, the resolved month token, and the loading/error state,
 * so it always runs first.
 *
 * Every other (optional) query is gated behind `activeTab` so the dashboard
 * no longer fires all endpoints at mount. Only the tab the user is viewing
 * fetches its data; React Query caches each result by key, so switching back to
 * a visited tab does not refetch. This spreads load on the backend instead of
 * hitting every endpoint simultaneously.
 *
 * @param {{ activeTab?: string }} [options] the queryKey of the visible tab
 *   (e.g. 'summary', 'trend', 'critical', 'volatility'). Tabs that fetch their
 *   own data via dedicated hooks (hqProductivity, matrix, creditNotes) do not
 *   need to be enabled here.
 */
export function useFieldSales({ activeTab = 'summary' } = {}) {
  const filters = useFieldSalesFilters((s) => s.applied);

  // The Overview tab renders the paged breakdown table (its own hook) plus the
  // Performance Analysis section fed by `summary`. `breakdown` here backs the
  // Overview section, so it loads alongside summary when Overview is active.
  const summary = useQuery({
    queryKey: [FS_SUMMARY_KEY, filters],
    queryFn: () => fieldSalesApi.getSummary(filters),
  });

  // NOTE: /summary/hqs is NOT fetched here. It only makes sense with a
  // group+band pair (band drill-down); calling it bare returns 400. It is
  // loaded exclusively by useFieldSalesSummaryHqsBand (enabled when the band
  // modal opens with both selectors set).

  const breakdown = useQuery({
    queryKey: [FS_BREAKDOWN_KEY, filters],
    queryFn: () => fieldSalesApi.getBreakdown(filters),
    enabled: activeTab === 'summary',
  });

  const trend = useQuery({
    queryKey: [FS_TREND_KEY, filters],
    queryFn: () => fieldSalesApi.getTrend(filters),
    enabled: activeTab === 'trend',
  });

  const hqProductivity = useQuery({
    queryKey: [FS_HQ_PRODUCTIVITY_KEY, filters],
    queryFn: () => fieldSalesApi.getHqProductivity(filters),
    enabled: activeTab === 'hqProductivity',
  });

  const matrix = useQuery({
    queryKey: [FS_MATRIX_KEY, filters],
    queryFn: () => fieldSalesApi.getMatrix(filters),
    enabled: activeTab === 'matrix',
  });

  const critical = useQuery({
    queryKey: [FS_CRITICAL_KEY, filters],
    queryFn: () => fieldSalesApi.getCritical(filters),
    enabled: activeTab === 'critical',
  });

  const volatility = useQuery({
    queryKey: [FS_VOLATILITY_KEY, filters],
    queryFn: () => fieldSalesApi.getVolatility(filters),
    enabled: activeTab === 'volatility',
  });

  const creditNotes = useQuery({
    queryKey: [FS_CREDIT_NOTES_KEY, filters],
    queryFn: () => fieldSalesApi.getCreditNotes(filters),
    enabled: activeTab === 'creditNotes',
  });

  return {
    summary,
    breakdown,
    trend,
    hqProductivity,
    matrix,
    critical,
    volatility,
    creditNotes,
    // Page-level flags driven ONLY by the required `summary` call, so a failed
    // optional section never flips the whole page to loading/error.
    isLoading: summary.isLoading || summary.isFetching,
    isError: summary.isError,
  };
}

/**
 * Paged breakdown by dimension for the Overview table. `dim` is 'brand'
 * (default) or 'sku'; `page` is 1-based. Reads the applied filters and keeps
 * previous data on page/dim changes so the table doesn't flash empty.
 *
 * @param {{ dim?: 'brand'|'sku', page?: number, pageSize?: number }} [options]
 */
export function useFieldSalesBreakdown({ dim = 'brand', page = 1, pageSize = 50 } = {}) {
  const filters = useFieldSalesFilters((s) => s.applied);
  return useQuery({
    queryKey: [FS_BREAKDOWN_PAGED_KEY, dim, page, pageSize, filters],
    queryFn: () => fieldSalesApi.getBreakdown(filters, { dim, page, pageSize }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Band drill-down list for the Overview band tiles. Loads the HQ-level rows for
 * one KPI band from /summary/hqs, selected by exactly `group` (the KPI card:
 * growth | achievement | pcpm) and `band` (the wire band id). Idle until
 * `enabled` (modal open) and both selectors are supplied.
 *
 * @param {{ group?: 'growth'|'achievement'|'pcpm', band?: string }|null} descriptor
 * @param {{ enabled?: boolean }} [options]
 */
export function useFieldSalesSummaryHqsBand(descriptor, { enabled = true } = {}) {
  const filters = useFieldSalesFilters((s) => s.applied);
  const { group, band } = descriptor || {};
  const isEnabled = Boolean(enabled && group && band);
  return useQuery({
    queryKey: [FS_SUMMARY_HQS_BAND_KEY, group, band, filters],
    queryFn: () => fieldSalesApi.getSummaryHqs(filters, { group, band }),
    enabled: isEnabled,
  });
}

/**
 * HQ Productivity Top/Bottom N for the HQ Productivity tab. `n` is the
 * Top/Bottom size (10 | 20 | 30 | 50 | 100) and is sent to the endpoint. Reads
 * the applied filters and keeps previous data on `n` changes so the tables
 * don't flash empty.
 *
 * @param {number} [n=20]
 */
export function useFieldSalesHqProductivity(n = 20) {
  const filters = useFieldSalesFilters((s) => s.applied);
  return useQuery({
    queryKey: [FS_HQ_PRODUCTIVITY_N_KEY, n, filters],
    queryFn: () => fieldSalesApi.getHqProductivity(filters, { n }),
    placeholderData: keepPreviousData,
  });
}

/**
 * PCPM × Growth Matrix for the Matrix tab. `quad` (HH|HL|LH|LL) selects which
 * quadrant's rows the endpoint returns; the counts and thresholds come back
 * every time (quadrant-independent). Reads the applied filters and keeps
 * previous data on quad changes so the panel doesn't flash empty.
 *
 * @param {'HH'|'HL'|'LH'|'LL'} [quad='HH']
 */
export function useFieldSalesMatrix(quad = 'HH') {
  const filters = useFieldSalesFilters((s) => s.applied);
  return useQuery({
    queryKey: [FS_MATRIX_QUAD_KEY, quad, filters],
    queryFn: () => fieldSalesApi.getMatrix(filters, { quad }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Credit Notes (sales leakage) for the Credit Notes tab. `type` selects the
 * leakage type sent on the wire (expiry | sales_return | rate_diff) and drives
 * which rows the endpoint returns; the totals (all three types) come back every
 * time. Reads the applied filters and keeps previous data on type changes.
 *
 * @param {'expiry'|'sales_return'|'rate_diff'} [type='expiry']
 */
export function useFieldSalesCreditNotes(type = 'expiry') {
  const filters = useFieldSalesFilters((s) => s.applied);
  return useQuery({
    queryKey: [FS_CREDIT_NOTES_TYPE_KEY, type, filters],
    queryFn: () => fieldSalesApi.getCreditNotes(filters, { type }),
    placeholderData: keepPreviousData,
  });
}
